import { ChevronRight, Inbox } from "lucide-react";
import { formatDashboardCount } from "@/features/dashboard/metricFormatters";
import type {
  DashboardAvailability,
  DashboardOperationalLoadPreview,
} from "@/features/dashboard/types";
import { DashboardSection } from "./DashboardSection";
import { DashboardState } from "./DashboardState";
import "./DashboardLedger.css";

const OLDEST_LOADS_CSS = `
  .dashboard-oldest-loads {
    height: 100%;
    min-width: 0;
  }

  .dashboard-oldest-loads__list {
    display: grid;
    margin: 0;
    min-width: 0;
    padding: 0;
    list-style: none;
  }

  .dashboard-oldest-loads__item {
    min-width: 0;
  }

  .dashboard-oldest-loads__row {
    appearance: none;
    align-items: center;
    width: 100%;
    min-width: 0;
    min-height: 64px;
    padding: 0.75rem 0.2rem;
    border: 0;
    border-top: 1px solid var(--ledger-border);
    background: transparent;
    color: var(--ledger-text);
    cursor: pointer;
    display: grid;
    gap: 0.35rem 0.7rem;
    grid-template-columns: minmax(0, 1fr) minmax(6.5rem, auto) 1.1rem;
    text-align: left;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      transform 120ms ease;
  }

  .dashboard-oldest-loads__item:first-child .dashboard-oldest-loads__row {
    border-top: 0;
  }
  .dashboard-oldest-loads__row:hover {
    background: color-mix(in srgb, var(--ledger-accent) 7%, var(--ledger-surface));
  }
  .dashboard-oldest-loads__row:active { transform: translateY(1px); }
  .dashboard-oldest-loads__row:focus-visible {
    outline: 2px solid var(--ledger-accent);
    outline-offset: 2px;
  }

  .dashboard-oldest-loads__identity {
    display: grid;
    gap: 0.2rem;
    min-width: 0;
  }

  .dashboard-oldest-loads__name {
    min-width: 0;
    overflow: hidden;
    color: var(--ledger-text);
    font-family: "DM Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 750;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dashboard-oldest-loads__status {
    color: var(--ledger-muted);
    font-family: "DM Sans", sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .dashboard-oldest-loads__timing {
    display: grid;
    gap: 0.2rem;
    justify-items: end;
    min-width: 0;
  }

  .dashboard-oldest-loads__age {
    color: var(--ledger-text);
    font-family: "DM Sans", sans-serif;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    font-weight: 800;
    line-height: 1.25;
    white-space: nowrap;
  }

  .dashboard-oldest-loads__date {
    color: var(--ledger-muted);
    font-family: "DM Sans", sans-serif;
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
  }

  .dashboard-oldest-loads__empty {
    align-items: center;
    color: var(--ledger-muted);
    display: flex;
    font-family: "DM Sans", sans-serif;
    font-size: 0.88rem;
    gap: 0.55rem;
    min-height: 7rem;
  }

  @media (max-width: 390px) {
    .dashboard-oldest-loads__row {
      align-items: start;
      gap: 0.35rem 0.5rem;
      grid-template-columns: minmax(0, 1fr) 1rem;
      padding-block: 0.7rem;
    }
    .dashboard-oldest-loads__name {
      display: -webkit-box;
      overflow: hidden;
      overflow-wrap: anywhere;
      text-overflow: clip;
      white-space: normal;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    .dashboard-oldest-loads__timing {
      grid-column: 1 / -1;
      justify-items: start;
    }
    .dashboard-oldest-loads__row > svg {
      grid-column: 2;
      grid-row: 1;
      justify-self: end;
    }
  }

  @media (max-width: 320px) {
    .dashboard-oldest-loads__row { padding-inline: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .dashboard-oldest-loads__row {
      transition-duration: 0.01ms;
      transform: none !important;
    }
  }
`;

export interface DashboardOldestLoadsProps {
  loads: readonly DashboardOperationalLoadPreview[];
  status?: DashboardAvailability;
  now?: Date;
  onOpenLoad: (load: DashboardOperationalLoadPreview) => void;
  onViewAll: () => void;
  onRetry?: () => void;
}

const DAY_IN_MS = 24 * 60 * 60 * 1_000;

const stateLabel = (state: DashboardOperationalLoadPreview["state"]) =>
  state === "in_progress" ? "Em andamento" : "Não iniciada";

const openedDate = (value: string) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));

const loadAge = (createdAt: string, now: Date) => {
  const created = new Date(createdAt).getTime();
  const elapsed = Math.max(0, now.getTime() - created);
  const days = Math.floor(elapsed / DAY_IN_MS);
  const formatted = formatDashboardCount(days);
  return {
    days,
    visible:
      days === 0 ? "Hoje" : days === 1 ? "1 dia" : `${formatted.full} dias`,
  };
};

export function DashboardOldestLoads({
  loads,
  status = "available",
  now = new Date(),
  onOpenLoad,
  onViewAll,
  onRetry,
}: DashboardOldestLoadsProps) {
  const ordered = [...loads].sort(
    (left, right) =>
      new Date(left.created_at).getTime() -
      new Date(right.created_at).getTime(),
  );

  return (
    <>
      <style>{OLDEST_LOADS_CSS}</style>
      <DashboardSection
        title="Abertas há mais tempo"
        className="dashboard-oldest-loads"
        action={
          status === "available" ? (
            <button
              type="button"
              className="dashboard-ledger-action"
              aria-label="Ver todas as cargas abertas"
              onClick={onViewAll}
            >
              Ver todas
            </button>
          ) : undefined
        }
      >
        {status === "error" ? (
          <DashboardState
            status="error"
            scope="section"
            title="Falha ao carregar esta seção"
            message="Não foi possível carregar as cargas abertas."
            onRetry={onRetry}
          />
        ) : status === "unavailable" ? (
          <DashboardState
            status="empty"
            scope="section"
            title="Seção indisponível"
            message="Esta seção está indisponível."
          />
        ) : ordered.length === 0 ? (
          <div className="dashboard-oldest-loads__empty" role="status">
            <Inbox aria-hidden="true" size={18} />
            Nenhuma carga aberta.
          </div>
        ) : (
          <ul className="dashboard-oldest-loads__list">
            {ordered.map((load) => {
              const status = stateLabel(load.state);
              const age = loadAge(load.created_at, now);
              const date = openedDate(load.created_at);
              return (
                <li
                  className="dashboard-oldest-loads__item"
                  key={load.order_id}
                >
                  <button
                    type="button"
                    className="dashboard-oldest-loads__row"
                    data-testid={`dashboard-oldest-load-${load.order_id}`}
                    data-load-id={load.order_id}
                    aria-label={`Abrir ${load.label}; ${status}; aberta em ${date}; ${age.visible}`}
                    onClick={() => onOpenLoad(load)}
                  >
                    <span className="dashboard-oldest-loads__identity">
                      <span
                        className="dashboard-oldest-loads__name"
                        title={load.label}
                      >
                        {load.label}
                      </span>
                      <span
                        className="dashboard-oldest-loads__status"
                        data-oldest-status
                        data-scale="medium"
                      >
                        {status}
                      </span>
                    </span>
                    <span className="dashboard-oldest-loads__timing">
                      <strong
                        className="dashboard-oldest-loads__age"
                        data-oldest-age
                        data-scale="medium"
                      >
                        {age.visible}
                      </strong>
                      <span className="dashboard-oldest-loads__date">
                        Aberta em {date}
                      </span>
                    </span>
                    <ChevronRight aria-hidden="true" size={17} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DashboardSection>
    </>
  );
}
